import { QueryKeys } from '@common/queries/common';
import { InitializeInMemorySearchQuery, InMemorySearchFiltersQuery } from '@common/queries/lineage';
import { theme } from '@prophecy/theme';
import { Stack, Container, Text, Columns, Collapse, Spinner, Centered, CollapseHeader, Empty } from '@prophecy/ui-v3';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as LineageImage } from '../../../assets/lineage.svg?react';
import { useGraphQlQuery, makeGraphQlCall } from '../../../data/util';
import { LineageSearchAllResults } from './contants';
import { LineageSearchFilter } from './Filter';
import { LineageSearchInput } from './LineageSearchInput';
import { EntityMatch, ColumnMatch, useLineageSearch } from './SearchResult';
import { AppliedFilters, SearchFilters } from './types';

const StyledFormWrapper = styled(Stack)`
  width: 720px;
`;

const StyledEmptySearchPage = styled(Stack)`
  padding-top: 160px;
`;

const StyledLineageImage = styled(LineageImage)`
  width: 100%;
`;

const StyledCollapse = styled(Collapse)`
  ${CollapseHeader} {
    padding: 0 ${theme.spaces.x12};
  }
`;

const LineageSearchPageContainer = styled.div`
  background: ${theme.colors.gray50};
  padding-bottom: ${theme.spaces.x40};
  overflow: auto;
  height: 100%;
`;

// Pre-populate the in-memory search cache when user focuses on search bar
function useInitializeSearch() {
  const [initialized, setInitialized] = useState(false);

  const initialize = usePersistentCallback(async () => {
    if (!initialized) {
      try {
        await makeGraphQlCall(InitializeInMemorySearchQuery);
        setInitialized(true);
      } catch {
        // Fire-and-forget
      }
    }
  });

  return initialize;
}

function EmptySearchPage() {
  const initialize = useInitializeSearch();

  return (
    <StyledEmptySearchPage alignY='center' gap='100px'>
      <StyledFormWrapper gap={theme.spaces.x12}>
        <LineageSearchInput onFocus={initialize} />
        <Text level='sm'>Search by Column, Dataset or Pipeline</Text>
      </StyledFormWrapper>
      <StyledLineageImage />
    </StyledEmptySearchPage>
  );
}

const DEFAULT_FILTERS: SearchFilters = {
  entityFilters: [],
  projectFilters: [],
  totalNameHits: 0,
  totalColumnHits: 0
};

const DEFAULT_APPLIED_FILTERS: AppliedFilters = {
  entityKinds: [LineageSearchAllResults],
  projectIds: []
};

function SearchView({ searchStr }: { searchStr: string }) {
  const [appliedFilters, setFilters] = useState<AppliedFilters>(DEFAULT_APPLIED_FILTERS);

  const variables = { query: searchStr };

  const { data: filtersData, isLoading: isFiltersLoading } = useGraphQlQuery(
    [QueryKeys.LineageSearch, 'filters', variables],
    InMemorySearchFiltersQuery,
    { refetchOnWindowFocus: false },
    variables
  );

  const filters: SearchFilters = useMemo(() => {
    const searchFilters = filtersData?.InMemorySearchFilters as SearchFilters | undefined;
    if (!searchFilters) {
      return DEFAULT_FILTERS;
    }

    return {
      entityFilters: searchFilters.entityFilters ?? [],
      projectFilters: searchFilters.projectFilters ?? [],
      totalNameHits: searchFilters.totalNameHits ?? 0,
      totalColumnHits: searchFilters.totalColumnHits ?? 0
    };
  }, [filtersData]);

  const { entityResults, columnResults, isLoading, loadMore } = useLineageSearch(searchStr, appliedFilters);

  useEffect(() => {
    setFilters(DEFAULT_APPLIED_FILTERS);
  }, [searchStr]);

  const showEntitySection = entityResults.results.length > 0 || isLoading;
  const showColumnSection = columnResults.results.length > 0 || isLoading;
  const hasNoResults = !showEntitySection && !showColumnSection && !isLoading;

  return (
    <Stack padding={`${theme.sizes.x48} 0 0`} gap={theme.sizes.x56} alignY='center'>
      <Stack alignY='center' width='100%'>
        <StyledFormWrapper>
          <LineageSearchInput value={searchStr} />
        </StyledFormWrapper>
      </Stack>
      <Container>
        {isFiltersLoading ? (
          <Centered>
            <Spinner />
          </Centered>
        ) : (
          <Columns gap={theme.spaces.x40}>
            {filters.totalColumnHits || filters.totalNameHits ? (
              <Columns.Column width='220px'>
                <LineageSearchFilter filters={filters} appliedFilters={appliedFilters} onChange={setFilters} />
              </Columns.Column>
            ) : null}
            <Columns.Column width='1fr'>
              <Stack gap={theme.spaces.x24}>
                {showEntitySection && (
                  <StyledCollapse defaultOpen={true} header={<Text level='sm'>Entity match</Text>}>
                    <EntityMatch
                      results={entityResults.results}
                      totalCount={entityResults.totalCount}
                      isLoading={isLoading}
                      loadMore={loadMore}
                    />
                  </StyledCollapse>
                )}

                {showColumnSection && (
                  <StyledCollapse defaultOpen={true} header={<Text level='sm'>Column name match</Text>}>
                    <ColumnMatch
                      results={columnResults.results}
                      totalCount={columnResults.totalCount}
                      isLoading={isLoading}
                      loadMore={loadMore}
                    />
                  </StyledCollapse>
                )}

                {hasNoResults && (
                  <Centered>
                    <Empty description='No search results found.' size='m' />
                  </Centered>
                )}
              </Stack>
            </Columns.Column>
          </Columns>
        )}
      </Container>
    </Stack>
  );
}

export default function LineageSearchPage() {
  const [searchParam] = useSearchParams();

  const searchStr = searchParam.get('search');

  return (
    <LineageSearchPageContainer>
      {searchStr ? <SearchView searchStr={searchStr} /> : <EmptySearchPage />}
    </LineageSearchPageContainer>
  );
}
